import React, { useState, useRef, useEffect } from 'react';
import projectsData from '../data/projects.json';
import '../stylePro.css';
import { AnimatedSection } from './AnimatedSection'; // Adjust path if needed
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

export const Projects = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [applyingProject, setApplyingProject] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [number, setNumber] = useState('');
  const [email, setEmail] = useState('');
  const [resume, setResume] = useState(null);
  const [coverLetter, setCoverLetter] = useState(null);
  const [dataPrivacy, setDataPrivacy] = useState(false);
  const [proudProject, setProudProject] = useState('');
  const [additionalMessage, setAdditionalMessage] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const applicationRef = useRef(null);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
  const scrollToApplication = () => {
    if (applicationRef.current) {
      const offsetTop =
        applicationRef.current.getBoundingClientRect().top +
        window.scrollY -
        100; // 100px offset
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };
  const toggleProject = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleCheckboxChange = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (type === 'resume') setResume(file);
    if (type === 'coverLetter') setCoverLetter(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append('projectName', applyingProject.name);
    formData.append('positions', JSON.stringify(selectedRoles));
    formData.append('message', message);
    formData.append('name', name);
    formData.append('surname', surname);
    formData.append('number', number);
    formData.append('email', email);
    formData.append('resume', resume);
    formData.append('coverLetter', coverLetter);
    formData.append('dataPrivacy', dataPrivacy.toString());
    formData.append('proudProject', proudProject);
    formData.append('additionalMessage', additionalMessage);

    try {
      const response = await fetch('https://mahanc.com/apply.php', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.status === 201) {
        setSubmissionStatus('Application submitted successfully!');
        setSelectedRoles([]);
        setMessage('');
        setName('');
        setSurname('');
        setNumber('');
        setEmail('');
        setResume(null);
        setCoverLetter(null);
        setDataPrivacy(false);
        setProudProject('');
        setAdditionalMessage('');
      } else {
        setSubmissionStatus(`Error submitting application: ${result.message}`);
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmissionStatus('Error submitting application.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        id="projects"
        className="projects-container"
        style={{ padding: '160px 0' }}
      >
        <div className="section-title text-center">
          <AnimatedSection>
            <h2 className="projects-title">Projects</h2>
          </AnimatedSection>
        </div>

        <div className="projects-list">
          {projectsData.map((project, index) => (
            <div
              className={`project-card ${
                project.status === 'in progress' ? 'disabled' : ''
              }`}
              key={index}
            >
              <div
                className="project-header"
                onClick={() => {
                  if (project.status === 'open') toggleProject(index);
                }}
              >
                {project.name}{' '}
                {project.status === 'in progress' && (
                  <span className="in-progress-label"> (In Progress)</span>
                )}
              </div>

              {openIndex === index && project.status === 'open' && (
                <div className="project-details">
                  <p>
                    <strong>Description:</strong>{' '}
                    <span>{project.description}</span>
                  </p>
                  <p>
                    <strong>Place of work:</strong>{' '}
                    <span>{project.placeOfWork}</span>
                  </p>
                  <p>
                    <strong>Benefits:</strong> <span>{project.benefits}</span>
                    {project.roles && project.roles.length > 0 ? (
                      <div className="roles-chart-container">
                        {(() => {
                          const dataValues = project.roles.map(
                            (role) => role.share
                          );
                          const total = dataValues.reduce(
                            (sum, val) => sum + val,
                            0
                          );

                          // Labels with percentages
                          const labelsWithPercent = project.roles.map(
                            (role) =>
                              `${role.title} (${((role.share / total) * 100).toFixed(1)}%)`
                          );

                          const data = {
                            labels: labelsWithPercent,
                            datasets: [
                              {
                                label: 'Role Share',
                                data: dataValues,
                                backgroundColor: [
                                  '#4e79a7',
                                  '#f28e2b',
                                  '#e15759',
                                  '#76b7b2',
                                  '#59a14f',
                                  '#edc948',
                                  '#b07aa1',
                                  '#ff9da7',
                                ],
                                borderColor: '#fff',
                                borderWidth: 2,
                              },
                            ],
                          };

                          const options = {
                            responsive: true,
                            plugins: {
                              legend: {
                                position: 'bottom',
                                labels: {
                                  boxWidth: 12,
                                  font: { size: 14 },
                                },
                              },
                              tooltip: {
                                callbacks: {
                                  label: function (context) {
                                    const percentage = (
                                      (context.raw / total) *
                                      100
                                    ).toFixed(1);
                                    return `${context.label}: ${percentage}%`;
                                  },
                                },
                              },
                              datalabels: {
                                color: '#fff',
                                formatter: (value) => {
                                  const percentage = (
                                    (value / total) *
                                    100
                                  ).toFixed(1);
                                  return `${percentage}%`;
                                },
                                font: {
                                  weight: 'bold',
                                  size: 14,
                                },
                              },
                            },
                            interaction: {
                              mode: 'none', // disables hover interactions
                            },
                            events: [], // disables clicks entirely
                          };

                          return <Pie data={data} options={options} />;
                        })()}
                      </div>
                    ) : (
                      <p>
                        <span>No roles listed.</span>
                      </p>
                    )}
                  </p>

                  <p>
                    <strong>Full job description:</strong>{' '}
                    <span>{project.fullJobDescription}</span>
                  </p>

                  <p>
                    <strong>Your profile:</strong>
                  </p>
                  <ul>
                    {Object.entries(project.yourProfile).map(
                      ([role, description]) => (
                        <li key={role}>
                          <strong>
                            {role.charAt(0).toUpperCase() + role.slice(1)}:
                          </strong>{' '}
                          {description}
                        </li>
                      )
                    )}
                  </ul>

                  <p>
                    <strong>Requirements:</strong>
                  </p>
                  <ul>
                    {project.requirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>

                  <p>
                    <strong>What to expect:</strong>{' '}
                    <span>{project.whatToExpect}</span>
                    <span>{project.compensation}</span>
                  </p>

                  <p>
                    <strong>What we offer:</strong>{' '}
                    <span>{project.whatWeOffer}</span>
                  </p>

                  <p>
                    <strong>About us:</strong> <span>{project.aboutUs}</span>
                  </p>

                  <div className="project-action-buttons">
                    <button
                      className="apply-button"
                      onClick={() => {
                        setApplyingProject(project);
                        setSelectedRoles([]);
                        setMessage('');
                        setSubmissionStatus('');
                        setTimeout(scrollToApplication, 100);
                      }}
                    >
                      Apply for this project
                    </button>

                    <button
                      className="close-button"
                      onClick={() => {
                        setOpenIndex(null);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      title="Close"
                    >
                      ▲ Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {applyingProject && (
          <div className="application-form" ref={applicationRef}>
            <h3>Apply for: {applyingProject.name}</h3>

            {applyingProject.roles?.length > 0 && (
              <div className="form-group">
                <label>Select roles you're interested in:</label>
                {applyingProject.roles
                  .filter(
                    (role) =>
                      role.title !== 'Mahanc Company' &&
                      role.title !== 'Idea Giver / Founder'
                  )
                  .map((role, i) => (
                    <div className="checkbox-item" key={i}>
                      <label>
                        <input
                          type="checkbox"
                          value={role.title}
                          checked={selectedRoles.includes(role.title)}
                          onChange={() => handleCheckboxChange(role.title)}
                        />
                        {` ${role.title} (${role.share})`}
                      </label>
                    </div>
                  ))}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginTop: '100px' }}>
                <label>Name:</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Surname:</label>
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number:</label>
                <input
                  type="tel"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Resume (PDF):</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => handleFileChange(e, 'resume')}
                  required
                />
              </div>

              <div className="form-group">
                <label>Cover Letter (optional):</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => handleFileChange(e, 'coverLetter')}
                />
              </div>

              <div className="form-group">
                <label>Do you want to tell us anything? (optional):</label>
                <textarea
                  value={additionalMessage}
                  onChange={(e) => setAdditionalMessage(e.target.value)}
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>What project are you most proud of? (optional):</label>
                <textarea
                  value={proudProject}
                  onChange={(e) => setProudProject(e.target.value)}
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label
                  className="privacy-label"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <input
                    type="checkbox"
                    checked={dataPrivacy}
                    onChange={(e) => setDataPrivacy(e.target.checked)}
                    required
                  />
                  <span>
                    I consent to the use of my data for this application.{' '}
                    <a
                      href="/data-protection"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="privacy-link"
                    >
                      Read more
                    </a>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={loading}
                style={{ marginTop: '100px' }}
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>

            {submissionStatus && (
              <div
                className={`submission-status ${
                  submissionStatus.includes('success') ? 'success' : 'error'
                }`}
              >
                {submissionStatus}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
